import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { BrowserLocationEvent } from 'pf-frontend-common/src/module/browser/location.event';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { JsonApiDataInterface } from 'pf-frontend-common/src/module/json-api/data.interface';
import { JsonApiModelInterface } from 'pf-frontend-common/src/module/json-api/model.interface';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { StatsDataServiceInterface } from 'pf-frontend-common/src/service/stats-data/service.interface';
import { WindowLocationServiceInterface } from 'pf-frontend-common/src/service/window-location/service.interface';

import { AgentModel } from 'common/data/agent/model';
import { AgentStatsDataAdapter } from 'common/data/agent/stats/data-adapter';
import { AgentStoreEvent } from 'common/data/agent/store.event';
import { AgentStoreSearchOutputInterface } from 'common/data/agent/store/search/output.interface';
import { LeadStatsLeadContextAdapter } from 'common/data/lead/stats/lead-context-adapter';
import { AgentSerpQueryParamsAdapter } from 'common/module/agent-serp/query-params/adapter';
import { AgentSerpStatsPaginationAdapter } from 'common/module/agent-serp/stats/pagination-adapter';
import { AgentSerpStore } from 'common/module/agent-serp/store';
import { AgentSerpStoreParamsSearchInputAdapter } from 'common/module/agent-serp/store/params/search-input-adapter';
import { AgentSerpStoreStateParamsInterface } from 'common/module/agent-serp/store/state-params.interface';
import { DataStoreEvent } from 'common/module/data/store.event';
import { DataStoreDispatchEventInterface } from 'common/module/data/store/dispatch-event.interface';
import { LeadEmailForm } from 'common/module/lead/email/form';
import { LeadEmailFormEvent } from 'common/module/lead/email/form.event';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LoaderViewStore } from 'common/module/loader/view-store';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { AgentStoreServiceInterface } from 'common/service/agent-store/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { AgentTabListEvent } from 'desktop/module/agent/tab/list.event';
import { AgentTabListViewStore } from 'desktop/module/agent/tab/list/view-store';
import { AgentTabPaginationViewStore } from 'desktop/module/agent/tab/pagination/view-store';
import { AgentTabViewOptionsInterface } from 'desktop/module/agent/tab/view-options.interface';
import { ClientPageTabViewEvent } from 'desktop/module/client/page/tab/view.event';
import { ClientPageTabViewInterface } from 'desktop/module/client/page/tab/view.interface';

// TODO-FE[WEB-1602]: Refactor this to work with common AgentListViewStore
export class AgentTabView implements ClientPageTabViewInterface {
  /**
   * Main view element
   */
  private element: HTMLElement;

  /**
   * Model
   */
  private model: JsonApiModelInterface;

  /**
   * CSS Selectors
   */
  private cssSelector: DataKeyValueStringInterface = {
    agentList: '[data-qs="agents"]',
    pagination: '[data-qs="pagination"]',
    cta: '[data-qs="cta"]',
    loader: '[data-qs=loader]',
  };

  /**
   * Whether tab activated
   */
  private activated: boolean = false;

  /**
   * Constructor
   */
  constructor(
    protected eventEmitter: EventEmitterInterface,
    protected queryParamsAdapter: AgentSerpQueryParamsAdapter,
    protected windowLocationService: WindowLocationServiceInterface,
    protected agentListViewDynamic: ViewDynamicInterface<AgentTabListViewStore>,
    protected paginationViewDynamic: ViewDynamicInterface<AgentTabPaginationViewStore>,
    protected agentStoreService: AgentStoreServiceInterface,
    protected agentSerpStore: AgentSerpStore,
    protected leadEmailForm: LeadEmailForm,
    protected statsDataService: StatsDataServiceInterface,
    protected statsService: StatsServiceInterface,
    protected agentStatsDataAdapter: AgentStatsDataAdapter,
    protected loaderViewStateful: ViewDynamicInterface<LoaderViewStore>,
    protected agentSerpStoreParamsSearchInputAdapter: AgentSerpStoreParamsSearchInputAdapter
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: AgentTabViewOptionsInterface): void {
    this.element = options.element;
    this.model = options.model;

    // Initialize store
    this.initStore(options);

    // Initialize subviews
    this.initLoader();
    this.initPagination();
    this.initAgentList();
    this.initLeadEmailForm();

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    this.agentListViewDynamic.render();
    this.paginationViewDynamic.render();
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * Activates/deactivates tab view
   *
   * @param activated - whether tab is activated
   */
  public setActivated(activated: boolean): void {
    this.activated = activated;

    // Enable/disable list
    this.agentListViewDynamic.getViewStore().updateDisabled(!activated);
  }

  /**
   * Get stats context local to this view
   */
  protected getStatsContext(): StatsContextLocalInterface {
    return {
      pagination: new AgentSerpStatsPaginationAdapter(this.agentSerpStore.getState()).getData(),
    };
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.agentSerpStore.getEventEmitter().addListener(DataStoreEvent.updateState, this.onUpdateStateStoreParam);

    this.windowLocationService.getEventEmitter().addListener(BrowserLocationEvent.pop, this.onPopBrowserLocation);

    this.agentStoreService.getEventEmitter().addListener(AgentStoreEvent.searchStart, this.onSearchStartStore);
    this.agentStoreService.getEventEmitter().addListener(AgentStoreEvent.searchSuccess, this.onSearchSuccessStore);
    this.agentStoreService.getEventEmitter().addListener(AgentStoreEvent.searchFailure, this.onSearchFailureStore);
  }

  /**
   * Init loader
   */
  private initLoader(): void {
    this.loaderViewStateful.initialize({
      element: domQuerySelector(this.element, this.cssSelector.loader),
    });

    this.loaderViewStateful.getViewStore().center(false);
  }

  /**
   * Initialize store
   */
  private initStore(options: AgentTabViewOptionsInterface): void {
    this.agentSerpStore.updateParams({
      'page[number]': options.payload.meta.page,
      'filter[broker_id]': this.model.id,
      'page[limit]': options.payload.meta.per_page || options.form.itemsPerPage,
    });

    this.agentSerpStore.updateResults({
      count: options.payload.meta.total_count,
      list: options.payload.data.map((data: JsonApiDataInterface) => data.id),
    });
  }

  /**
   * Init agent list
   */
  private initAgentList(): void {
    this.agentListViewDynamic.initialize({
      element: domQuerySelector(this.element, this.cssSelector.agentList),
    });

    this.agentListViewDynamic.getViewStore().initialize();

    this.agentListViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(AgentTabListEvent.clickEmail, this.onClickEmail);
  }

  /**
   * Init pagination
   */
  private initPagination(): void {
    this.paginationViewDynamic.initialize({
      element: domQuerySelector(this.element, this.cssSelector.pagination),
    });

    this.paginationViewDynamic.getViewStore().initialize();
  }

  /**
   * Initialize lead email form
   */
  private initLeadEmailForm(): void {
    this.leadEmailForm.initialize();

    this.leadEmailForm.getEventEmitter().addListener(LeadEmailFormEvent.submit, this.onSubmitEmail);
  }

  /**
   * Open agent form
   */
  private openLeadForm(agentModel: AgentModel): void {
    this.leadEmailForm.initState();
    this.leadEmailForm.updateModel(agentModel);
    this.leadEmailForm.render();
  }

  /**
   * Perform search
   */
  private search(): void {
    this.agentStoreService.search(
      this.agentSerpStoreParamsSearchInputAdapter.getData(this.agentSerpStore.getState().params)
    );
  }

  /**
   * Clicked email button
   */
  private onClickEmail = (e: Event, agentModel: AgentModel): void => {
    this.openLeadForm(agentModel);
  };

  /**
   * A store search request has been made
   */
  private onSearchStartStore = (): void => {
    // Enable loader
    this.loaderViewStateful.getViewStore().enable();

    // Disable list
    this.agentListViewDynamic.getViewStore().updateDisabled(true);

    if (this.activated) {
      this.getEventEmitter().emit(ClientPageTabViewEvent.update);
    }
  };

  /**
   * A store search request has failed
   */
  private onSearchFailureStore = (): void => {
    // Disable loader
    this.loaderViewStateful.getViewStore().disable();

    // Enable list
    this.agentListViewDynamic.getViewStore().updateDisabled(false);
  };

  /**
   * A store search request has finished
   */
  private onSearchSuccessStore = (searchOutput: AgentStoreSearchOutputInterface): void => {
    // Update results
    this.agentSerpStore.updateResults({
      count: searchOutput.pagination.totalCount,
      list: searchOutput.agents.map((agent: AgentModel) => agent.id),
    });

    // Disable loader
    this.loaderViewStateful.getViewStore().disable();

    // Enable list
    this.agentListViewDynamic.getViewStore().updateDisabled(false);
  };

  /**
   * Trigger a sync
   */
  private onUpdateStateStoreParam = (storeDispatchEvent: DataStoreDispatchEventInterface): void => {
    if (storeDispatchEvent.action === this.agentSerpStore.updateParams) {
      // Perform search
      this.search();
    }
  };

  /**
   * Sync the store to the location when the URL updates
   */
  private onPopBrowserLocation = (): void => {
    let shouldUpdate = false;

    const params = this.agentSerpStore.getState().params;

    // Map query data to state params
    const qParams = this.queryParamsAdapter.toAgentSerpStoreStateParams(
      this.windowLocationService.getQueryData().agents || { brokerId: params['filter[broker_id]'] }
    );

    Object.keys(params).forEach((key: keyof AgentSerpStoreStateParamsInterface) => {
      if (qParams[key] && qParams[key] !== params[key]) {
        shouldUpdate = true;
      }
    });

    if (!shouldUpdate) {
      return;
    }

    this.agentSerpStore.updateParams(qParams);
  };

  /**
   * Submitted email form
   */
  private onSubmitEmail = (input: LeadEmailFormInputInterface): void => {
    const context: StatsContextLocalInterface = {
      ...this.getStatsContext(),
      lead: new LeadStatsLeadContextAdapter(input, false).getData(),
    };

    const model = this.leadEmailForm.getModel();

    // Log stats
    this.statsService.agentLeadSend(parseInt(model.id, 10), context);
  };
}
